import React from 'react';
import { Link } from 'react-router-dom';
import { useLivePageContent } from '@/hooks/usePageContent';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Reveal, RevealGroup, fadeUp, motion } from '@/components/public/anim';
import { ArrowRight, Leaf, Briefcase, Mountain, Check, HeartHandshake } from 'lucide-react';

const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

const DEFAULTS = {
  eyebrow: 'Lavora con noi',
  title: 'Costruisci la tua carriera nel cuore della Sila',
  lead: 'Cerchiamo professionisti dell\u2019ospitalit\u00e0 e guide del territorio che amano la Sila quanto noi. Scegli il tuo percorso e unisciti alla rete Sila Experiences.',
  heroImage: 'https://images.pexels.com/photos/28127038/pexels-photo-28127038.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1920',
  intro: 'Sila Experiences mette in rete strutture, ristoranti e guide del Parco per offrire esperienze autentiche. Che tu sia un professionista dell\u2019ospitalit\u00e0 o una guida ambientale, qui trovi gli strumenti digitali, la visibilit\u00e0 e il supporto per crescere.',
};

const PATHS = [
  {
    key: 'staff',
    icon: Briefcase,
    tag: 'Professionisti',
    title: 'Entra nel team Staff',
    desc: 'Receptionist, addetti alle pulizie, manutentori, cuochi e camerieri: candidati come professionista e ricevi incarichi dalle strutture della rete.',
    to: '/staff/registrati',
    cta: 'Candidati come Staff',
    img: 'https://images.pexels.com/photos/8006229/pexels-photo-8006229.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    key: 'guida',
    icon: Mountain,
    tag: 'Guide del Parco',
    title: 'Diventa Guida del Parco',
    desc: 'Sei una guida ambientale o escursionistica? Crea i tuoi itinerari, pubblicali sulla mappa 3D del Parco e ricevi prenotazioni dai visitatori.',
    to: '/guida/registrati',
    cta: 'Registrati come Guida',
    img: 'https://images.pexels.com/photos/17983542/pexels-photo-17983542.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
];

const BENEFITS = [
  'Visibilit\u00e0 sulla vetrina pubblica Sila Experiences',
  'Strumenti digitali gestionali inclusi',
  'Prenotazioni e pagamenti online',
  'Supporto dedicato del team',
];

export default function LavoraConNoi() {
  const ov = useLivePageContent('lavora-con-noi');

  const eyebrow = str(ov.eyebrow) || DEFAULTS.eyebrow;
  const title = str(ov.title) || DEFAULTS.title;
  const lead = str(ov.lead) || DEFAULTS.lead;
  const heroImage = str(ov.heroImage) || DEFAULTS.heroImage;
  const intro = str(ov.intro) || DEFAULTS.intro;
  // Le due card percorso: override opzionale da features[0]=Staff, features[1]=Guida
  const feats = Array.isArray(ov.features) ? ov.features : [];
  const paths = PATHS.map((p, i) => ({
    ...p,
    title: str(feats[i]?.title) || p.title,
    desc: str(feats[i]?.desc) || p.desc,
    img: str(feats[i]?.img) || p.img,
    tag: str(feats[i]?.tag) || p.tag,
  }));
  const bullets = Array.isArray(ov.bullets) && ov.bullets.length ? ov.bullets.map(str) : BENEFITS;

  return (
    <PublicLayout>
      <Seo title={title} description={lead} path="/lavora-con-noi" image={heroImage} />

      {/* HERO */}
      <section className="relative isolate flex min-h-[58vh] items-end overflow-hidden bg-[hsl(var(--brand-bark))] text-white" data-testid="lavora-hero">
        <img src={heroImage} alt={title} className="absolute inset-0 -z-10 h-full w-full object-cover" />
        <div aria-hidden className="absolute inset-0 -z-10" style={{ background: 'linear-gradient(180deg, hsl(150 42% 8% / 0.35) 0%, hsl(150 42% 8% / 0.55) 55%, hsl(150 42% 8% / 0.9) 100%)' }} />
        <div className="grain-overlay absolute inset-0 -z-10 opacity-40" />
        <div className="sila-section w-full pb-14 pt-32">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] backdrop-blur-md sm:text-xs">
            <Leaf className="h-3.5 w-3.5" style={{ color: 'hsl(var(--brand-sun))' }} />{eyebrow}
          </span>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight drop-shadow-[0_2px_20px_rgba(0,0,0,0.4)] sm:text-5xl lg:text-6xl">{title}</h1>
          <p className="mt-4 max-w-xl text-lg text-white/85">{lead}</p>
        </div>
      </section>

      {/* INTRO */}
      {intro && (
        <section className="sila-section py-14 sm:py-16">
          <div className="max-w-3xl">
            <p className="text-lg leading-relaxed text-muted-foreground sm:text-xl">{intro}</p>
          </div>
        </section>
      )}

      {/* DUE PERCORSI */}
      <section className="sila-section pb-4" data-testid="lavora-paths">
        <RevealGroup className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {paths.map((p) => {
            const Icon = p.icon;
            return (
              <motion.div key={p.key} variants={fadeUp}>
                <div className="group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-border bg-card shadow-[var(--shadow-sm)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]" data-testid={`lavora-path-${p.key}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img loading="lazy" src={p.img} alt={p.title} className="h-full w-full object-cover transition-transform duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform group-hover:scale-110" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, hsla(210,18%,8%,0.55) 100%)' }} />
                    <span className="absolute bottom-4 left-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg"><Icon className="h-6 w-6" /></span>
                    <span className="absolute left-4 top-4 rounded-full bg-background/85 px-3 py-1 text-xs font-semibold text-foreground backdrop-blur">{p.tag}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h2 className="font-display text-2xl font-semibold">{p.title}</h2>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                    <Button asChild size="lg" className="mt-5 w-full rounded-full" data-testid={`lavora-cta-${p.key}`}>
                      <Link to={p.to}>{p.cta}<ArrowRight className="ml-2 h-4 w-4" /></Link>
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </RevealGroup>
      </section>

      {/* BENEFIT */}
      <section className="sila-section py-16">
        <Reveal>
          <div className="grain-overlay overflow-hidden rounded-[28px] bg-primary p-8 text-primary-foreground sm:p-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide"><HeartHandshake className="h-3.5 w-3.5" />Perché unirti a noi</span>
            <h2 className="mt-4 font-display text-2xl font-bold sm:text-3xl">I vantaggi della rete Sila Experiences</h2>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 rounded-2xl bg-primary-foreground/10 p-4">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-foreground text-primary"><Check className="h-3.5 w-3.5" /></span>
                  <span className="text-sm leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>
    </PublicLayout>
  );
}
